import { Box, Flex, Input } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

interface OTPInputProps {
	length?: number;
	onChange: (otp: string) => void;
	value?: string;
	disabled?: boolean;
}

const OTPInput = ({
	length = 6,
	onChange,
	value = '',
	disabled = false,
}: OTPInputProps) => {
	const [otp, setOtp] = useState<string[]>(
		value?.split('').slice(0, length).concat(Array(length - value.length).fill('')),
	);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	useEffect(() => {
		if (value) {
			const newOtp = value?.split('').slice(0, length);
			while (newOtp.length < length) {
				newOtp.push('');
			}
			setOtp(newOtp);
		}
	}, [value, length]);

	const handleChange = (index: number, newValue: string) => {
		if (newValue && !/^\d$/.test(newValue)) {
			return;
		}

		const newOtp = [...otp];
		newOtp[index] = newValue;
		setOtp(newOtp);

		const otpString = newOtp.join('');
		onChange(otpString);

		if (newValue && index < length - 1) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (
		index: number,
		e: React.KeyboardEvent<HTMLInputElement>,
	) => {
		if (e.key === 'Backspace' && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (
		index: number,
		e: React.ClipboardEvent<HTMLInputElement>,
	) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData('text').slice(0, length);
		const pastedDigits = pastedData?.split('').filter((char) => /^\d$/.test(char));

		if (pastedDigits.length > 0) {
			const newOtp = [...otp];
			pastedDigits.forEach((digit, i) => {
				if (index + i < length) {
					newOtp[index + i] = digit;
				}
			});
			setOtp(newOtp);

			const otpString = newOtp.join('');
			onChange(otpString);

			const nextIndex = Math.min(index + pastedDigits.length, length - 1);
			inputRefs.current[nextIndex]?.focus();
		}
	};

	return (
		<Flex gap={4} justifyContent="center" alignItems="center">
			{Array.from({ length }).map((_, index) => (
				<Box key={`otp-input-${index}-${Math.random()}`}>
					<Input
						ref={(el) => {
							inputRefs.current[index] = el;
						}}
						type="text"
						inputMode="numeric"
						maxLength={1}
						value={otp[index] || ''}
					  onChange={(e) => handleChange(index, e.target.value)}
					  onKeyDown={(e) => handleKeyDown(index, e)}
					  onPaste={(e) => handlePaste(index, e)}
						disabled={disabled}
						textAlign="center"
						fontSize="xl"
						fontWeight="bold"
						w="50px"
						h="50px"
						borderRadius="8px"
						border="2px solid"
						borderColor="brand.robotGrey"
						bg="brand.inputBg"
						_placeholder={{
							color: 'brand.inputPlaceholder',
						}}
						_focus={{
							borderColor: 'brand.solid',
							boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.1)',
						}}
						_disabled={{
							opacity: 0.6,
							cursor: 'not-allowed',
						}}
					/>
				</Box>
			))}
		</Flex>
	);
};

export default OTPInput;
